import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete, Tune } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, AccountService, ReportColumnData, ReportPresetData } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useLanguage } from '../common/GeneralUtils';
import { CheckboxGroupSection } from '../common/filter/CheckboxGroupSection';
import { WorkshopsFilterSection } from '../common/filter/WorkshopsFilterSection';
import { getOrderPages, getPassOrderPages } from '../utils/AccountUtils';
import { ALL_COLUMNS_PRESET_ID, COLUMN_GROUPS, columnGroupOf } from './reportPresets';

interface ReportPresetControlsProps {
  columns: ReportColumnData[];
  presets: ReportPresetData[];
  onPresetsChange: (presets: ReportPresetData[]) => void;
  selectedPresetId: string;
  onSelectedPresetIdChange: (presetId: string) => void;
}

/** A preset being edited. An empty `order_page_ids` means "all pages", so a selection of no pages needs its own flag. */
type DraftPreset = ReportPresetData & { hasNoPages?: boolean };

enum PresetTab {
  COLUMNS = 'columns',
  PAGES = 'pages',
  PASSES = 'passes',
}

export const ReportPresetControls: React.FC<ReportPresetControlsProps> = ({
  columns,
  presets,
  onPresetsChange,
  selectedPresetId,
  onSelectedPresetIdChange,
}) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [draftPresets, setDraftPresets] = useState<DraftPreset[]>([]);
  const [editedPresetIndex, setEditedPresetIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(PresetTab.COLUMNS);
  const [isSaving, setIsSaving] = useState(false);
  const { updateLocalAccount } = useGetAccountAuthenticated({});
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'));

  const columnLabels = new Map(columns.map((column) => [column.key, column.label]));
  const columnKeysByGroup = COLUMN_GROUPS.map((group) => ({
    group,
    keys: columns.map((column) => column.key).filter((key) => columnGroupOf(key) === group),
  })).filter(({ keys }) => keys.length > 0);

  const activeOrderPageIds = getOrderPages(account).map((page) => page.id);
  const passPages = getPassOrderPages(account);
  const passPageIds = passPages.map((page) => page.id);
  const passPageLabels = new Map(
    passPages.map((page) => [page.id, page.label?.[language] || page.label?.en || page.label?.he || '']),
  );

  const updatePreset = (index: number, changes: Partial<DraftPreset>) => {
    setDraftPresets((current) =>
      current.map((preset, presetIndex) => (presetIndex === index ? { ...preset, ...changes } : preset)),
    );
  };

  const openManage = () => {
    setDraftPresets(
      presets.map((preset) => ({
        ...preset,
        field_keys: [...(preset.field_keys ?? [])],
        order_page_ids: [...(preset.order_page_ids ?? [])],
      })),
    );
    setEditedPresetIndex(0);
    setIsManageOpen(true);
  };

  const addPreset = () => {
    setDraftPresets((current) => [
      ...current,
      { label: t('reportPresets.newPresetName'), field_keys: [], order_page_ids: [] },
    ]);
    setEditedPresetIndex(draftPresets.length);
  };

  const removePreset = (index: number) => {
    setDraftPresets((current) => current.filter((_, presetIndex) => presetIndex !== index));
    setEditedPresetIndex(0);
  };

  const savePresets = async () => {
    const named = draftPresets
      .filter((preset) => preset.label.trim().length > 0)
      .map(({ hasNoPages: _hasNoPages, ...preset }) => preset);
    setIsSaving(true);
    try {
      const updatedAccount: AccountData = await AccountService.updateReportPresets({ report_presets: named });
      const savedPresets = updatedAccount.report_presets ?? [];
      await updateLocalAccount(updatedAccount, { revalidate: false });
      onPresetsChange(savedPresets);
      if (!savedPresets.some((preset) => preset.id === selectedPresetId)) {
        onSelectedPresetIdChange(ALL_COLUMNS_PRESET_ID);
      }
      setIsManageOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const editedPreset = draftPresets[editedPresetIndex];
  const fieldKeys = editedPreset?.field_keys ?? [];
  const savedPageIds = editedPreset?.order_page_ids ?? [];
  const isAllPages = savedPageIds.length === 0 && !editedPreset?.hasNoPages;
  const selectedOrderPageIds = isAllPages ? null : savedPageIds.filter((id) => !passPageIds.includes(id));
  const selectedPassPageIds = isAllPages ? passPageIds : savedPageIds.filter((id) => passPageIds.includes(id));

  const setColumnGroup = (groupKeys: string[], selectedGroupKeys: string[]) => {
    updatePreset(editedPresetIndex, {
      field_keys: [...fieldKeys.filter((key) => !groupKeys.includes(key)), ...selectedGroupKeys],
    });
  };

  const setPages = (orderPageIds: string[] | null, passIds: string[]) => {
    if (orderPageIds === null && passIds.length === passPageIds.length) {
      updatePreset(editedPresetIndex, { order_page_ids: [], hasNoPages: false });
      return;
    }
    const pageIds = [...(orderPageIds ?? activeOrderPageIds), ...passIds];
    updatePreset(editedPresetIndex, { order_page_ids: pageIds, hasNoPages: pageIds.length === 0 });
  };

  const visibleTab = activeTab === PresetTab.PASSES && passPages.length === 0 ? PresetTab.COLUMNS : activeTab;
  const hasPresetWithoutPages = draftPresets.some((preset) => preset.hasNoPages);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="report-preset-label">{t('reportPresets.preset')}</InputLabel>
          <Select
            labelId="report-preset-label"
            label={t('reportPresets.preset')}
            value={selectedPresetId}
            onChange={(event) => onSelectedPresetIdChange(event.target.value)}>
            <MenuItem value={ALL_COLUMNS_PRESET_ID}>{t('reportPresets.allData')}</MenuItem>
            {presets.map((preset) => (
              <MenuItem key={preset.id} value={preset.id ?? preset.label}>
                {preset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title={t('reportPresets.manage')}>
          <IconButton onClick={openManage} aria-label={t('reportPresets.manage')}>
            <Tune />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={isManageOpen} onClose={() => setIsManageOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('reportPresets.manage')}</DialogTitle>
        <DialogContent>
          <Stack direction={isWideScreen ? 'row' : 'column'} spacing={3} alignItems="stretch">
            <Stack spacing={1} sx={{ minWidth: 220 }}>
              {draftPresets.map((preset, index) => (
                <Stack key={preset.id ?? `draft-${index}`} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    value={preset.label}
                    onChange={(event) => updatePreset(index, { label: event.target.value })}
                    onFocus={() => setEditedPresetIndex(index)}
                    error={preset.hasNoPages}
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': index === editedPresetIndex ? { bgcolor: 'mb.ground' } : {},
                    }}
                  />
                  <IconButton size="small" onClick={() => removePreset(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button onClick={addPreset}>{t('reportPresets.addPreset')}</Button>
            </Stack>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {editedPreset ? (
                <>
                  <Tabs value={visibleTab} onChange={(_, tab: PresetTab) => setActiveTab(tab)} variant="scrollable">
                    <Tab
                      value={PresetTab.COLUMNS}
                      label={`${t('reportPresets.tabs.columns')} (${fieldKeys.length || t('reportPresets.all')})`}
                    />
                    <Tab value={PresetTab.PAGES} label={t('reportPresets.tabs.pages')} />
                    {passPages.length > 0 && <Tab value={PresetTab.PASSES} label={t('reportPresets.tabs.passes')} />}
                  </Tabs>
                  {editedPreset.hasNoPages && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      {t('reportPresets.noPagesSelected')}
                    </Typography>
                  )}
                  <Box sx={{ height: isWideScreen ? '55vh' : 'auto', overflowY: 'auto' }}>
                    <List disablePadding>
                      {visibleTab === PresetTab.COLUMNS &&
                        columnKeysByGroup.map(({ group, keys }, groupIndex) => (
                          <CheckboxGroupSection<string>
                            key={group}
                            title={t(`reportPresets.columnGroups.${group}`)}
                            items={keys}
                            itemLabel={(key) => columnLabels.get(key) ?? key}
                            selected={fieldKeys.filter((key) => keys.includes(key))}
                            onChange={(selectedGroupKeys) => setColumnGroup(keys, selectedGroupKeys)}
                            withDivider={groupIndex < columnKeysByGroup.length - 1}
                          />
                        ))}
                      {visibleTab === PresetTab.PAGES && (
                        <WorkshopsFilterSection
                          account={account}
                          selectedIds={selectedOrderPageIds}
                          onChange={(orderPageIds) => setPages(orderPageIds, selectedPassPageIds)}
                        />
                      )}
                      {visibleTab === PresetTab.PASSES && (
                        <CheckboxGroupSection<string>
                          title={t('reportPresets.tabs.passes')}
                          items={passPageIds}
                          itemLabel={(id) => passPageLabels.get(id) ?? ''}
                          selected={selectedPassPageIds}
                          onChange={(passIds) => setPages(selectedOrderPageIds, passIds)}
                          withDivider={false}
                        />
                      )}
                    </List>
                  </Box>
                </>
              ) : (
                <Typography variant="body2">{t('reportPresets.emptyState')}</Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setIsManageOpen(false)}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={savePresets} disabled={isSaving || hasPresetWithoutPages}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
